import { jours } from "@/data/days";
import type { Creneau, Culte } from "@/types/Culte";
import type { Jour } from "@/types/day";

interface ProchainCulte extends Culte {
  creneau: Creneau;
  enCours: boolean;
  joursRestants: number;
}

export const getProchainCulte = (
  cultes: Culte[],
  maintenant: Date = new Date(),
): ProchainCulte | null => {
  const jourActuel = jours[maintenant.getDay()];

  const minutesActuelles = maintenant.getHours() * 60 + maintenant.getMinutes();

  const indexJourActuel = jours.indexOf(jourActuel);

  // Tous les cultes d'aujourd'hui
  const cultesAujourdhui = cultes.filter((culte) => culte.jour === jourActuel);

  // 1. Chercher un créneau encore en cours ou à venir aujourd'hui
  for (const culte of cultesAujourdhui) {
    for (const creneau of culte.creneaux) {
      const debut = heureEnMinutes(creneau.heureDebut);
      const fin = heureEnMinutes(creneau.heureFin);

      // Culte en cours
      if (minutesActuelles >= debut && minutesActuelles < fin) {
        return {
          ...culte,
          creneau,
          enCours: true,
          joursRestants: 0,
        };
      }

      // Culte à venir aujourd'hui
      if (minutesActuelles < debut) {
        return {
          ...culte,
          creneau,
          enCours: false,
          joursRestants: 0,
        };
      }
    }
  }

  // 2. Aucun culte aujourd'hui → chercher le prochain jour
  const prochainsCultes = cultes
    .map((culte) => {
      const indexJourCulte = jours.indexOf(culte.jour as Jour);

      const distance = (indexJourCulte - indexJourActuel + 7) % 7;

      return {
        culte,
        distance,
      };
    })
    .filter(({ distance }) => distance > 0)
    .sort((a, b) => a.distance - b.distance);

  if (prochainsCultes.length === 0) {
    return null;
  }

  const prochain = prochainsCultes[0];

  return {
    ...prochain.culte,
    creneau: prochain.culte.creneaux[0],
    enCours: false,
    joursRestants: prochain.distance,
  };
};

const heureEnMinutes = (heure: string): number => {
  const [h, m] = heure.replace("h", ":").split(":").map(Number);
  return h * 60 + m;
};

export const getDateForJour = (jour: Jour, today: Date = new Date()): Date => {
  const todayIndex = today.getDay();
  const targetIndex = jours.indexOf(jour);

  const difference = (targetIndex - todayIndex + 7) % 7;

  const date = new Date(today);

  date.setDate(today.getDate() + difference);

  return date;
};

/** « 2026-08-23 » → « 23 août 2026 ». Renvoie la valeur telle quelle si ce n'est pas une date ISO. */
export const formatDateLongue = (iso: string): string => {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
};

/** Trie une liste (prédications…) de la plus récente à la plus ancienne. */
export const plusRecentesDabord = <T extends { date: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => b.date.localeCompare(a.date));
